<template>
  <div id="splitter" class="splitter">
    <!-- Left pane : PP & Instructeur common part -->
    <div
      id="panel-left"
      :class="{ 'panel-left': canSeeInstructionPanel, 'panel-full': !canSeeInstructionPanel }"
    >
      <PageView
        :main-title="mainTitle"
        :sub-title="project ? project.nom : ''"
        :sub-title-is-editable="true"
      >
        <ContentView id="content">
          <LoadingSkeleton v-if="isLoading" :loading-text="$t('projectPage.loading')" />
          <!-- Project validation and save : handle validation & save button enablement -->
          <ProjectValidationAndSaveHandler
            v-else
            :project="project"
            :validation-activated="validationActivated"
            :show-validation-errors="showValidationErrors"
            :validation-request-date="lastValidationDate"
            :dirty="dirty"
            :readonly="readonly"
            :role="role"
            :is-porteur-projet="isPorteurProjet"
            :is-saving="isSaving"
            :px-from-bottom="pxFromBottom"
            :map-data-url="mapDataUrl"
            :is-pdf-export-done="exportingPdfDone"
            :hidden="modalShown"
            :images-url="imagesUrl"
            @validation-changed="projetAnnuelValidationChanged"
            @save-project="saveProject"
            @toggle-validation="togglevalidation"
            @send-inscription="sendInscription"
            @send-labellisation="sendLabellisation"
            @do-map-screenshot="doMapScreenshot"
            @clear-map-screenshot="clearMapScreenshot"
          >
            <!-- ProjetYearAndStatutSelection : handles year selection and statut change -->
            <ProjetYearAndStatutSelection
              v-if="loggedUser && project && project.id"
              :projet-annuel="project"
              :readonly="readonly"
              :role="role"
              :logged-user="loggedUser"
              :is-porteur-projet="isPorteurProjet"
              :etats="etats"
              @reload-project="reloadProjectFromServer"
              @show-confirm-modal="modalShown = true"
              @close-confirm-modal="modalShown = false"
            />

            <!-- Page content organised in section & subsections -->
            <div id="project-content" ref="projectContent">
              <!-- Inscription -->
              <ProjetInscriptionHolder
                v-if="project && project.id && project.typeDossier == 'DOSSIER_INSCRIPTION'"
                :current-scroll-y="currentScrollY"
                :project="project"
                :section="section"
                :active-tab="activeTab"
                :validation-activated="validationActivated"
                :show-validation-errors="showValidationErrors"
                :last-project-validation="lastProjectValidation"
                :readonly="readonly || (project.statut != 'BROUILLON' && isPorteurProjet)"
                :is-porteur-projet="isPorteurProjet"
                :px-from-bottom="pxFromBottom"
                @projet-annuel-updated="updateProject"
                @dirtify-project="dirtifyProject"
                @trigger-validation="triggerValidation"
              />
              <!-- Labellisation -->
              <ProjetLabellisationHolder
                v-else-if="project && project.id"
                :current-scroll-y="currentScrollY"
                :project="project"
                :section="section"
                :active-tab="activeTab"
                :validation-activated="validationActivated"
                :show-validation-errors="showValidationErrors"
                :last-project-validation="lastProjectValidation"
                :readonly="readonly"
                :role="role"
                :is-porteur-projet="isPorteurProjet"
                :px-from-bottom="pxFromBottom"
                :current-width="currentWidth"
                :is-map-screenshot-requested="mapScreenshotRequested"
                @projet-annuel-updated="updateProject"
                @dirtify-project="dirtifyProject"
                @reload-project="reloadProjectFromServer"
                @toggle-validation="togglevalidation"
                @images-url-changed="changeImageUrl"
                @screenshot-ready="screenshotRequestDone"
                @trigger-validation="triggerValidation"
              />
            </div>
          </ProjectValidationAndSaveHandler>
        </ContentView>
      </PageView>
    </div>
    <div v-if="canSeeInstructionPanel" id="panel-separator">
      <div class="resize-symbol"></div>
    </div>
    <!-- Right Pane - instructor-only pane-->
    <div v-if="canSeeInstructionPanel" id="panel-right">
      <button
        v-if="instructionPanelWidth > instructionPanelAutoCloseWidth"
        class="panel-right-close"
        @click="collapseResizablePanel(true, false, false)"
      >
        <b-icon icon="times" size="is-small" />
      </button>
      <InstructionPanel
        :project="project"
        :logged-user="loggedUser"
        :min-visible-width="instructionPanelAutoCloseWidth"
        :instruction-panel-width="instructionPanelWidth"
        @new-menu-selected="collapseResizablePanel(false, false, false)"
        @evaluation-saved="reloadProjectFromServer"
      />
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue"
import { debounce } from "lodash"
import type { EtatProjet } from "@/model/bean/GeneratedDTOs"
import {
  ProjetAnnuelDTO,
  ProjetInscriptionDTO,
  ProjetPatchDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import InstructionPanel from "@/pages/instruction/side-panel/InstructionPanel.vue"
import ProjectValidationAndSaveHandler, {
  ProjetAnnualValidation,
} from "@/pages/projet-page/commons/ProjectValidationAndSaveHandler.vue"
import ProjetInscriptionHolder from "@/pages/projet-page/inscription/ProjetInscriptionHolder.vue"
import ProjetLabellisationHolder from "@/pages/projet-page/labellisation/ProjetLabellisationHolder.vue"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { LoginService } from "@/services/rest/login/LoginService"
import { ProjectService } from "@/services/rest/project/ProjectService"
import { InstructionService } from "@/services/rest/instruction/InstructionService"
import { UsersService } from "@/services/rest/users/UsersService"
import ContentView from "@/views/commons/ContentView.vue"
import PageView from "@/views/commons/PageView.vue"
import LoadingSkeleton from "@/views/generic_components/LoadingSkeleton.vue"
import ProjetYearAndStatutSelection from "./ProjetYearAndStatutSelection.vue"
import { eventBus } from "@/services/EventBus"
import { BIcon } from "buefy"

export default defineComponent({
  name: "ProjetDetailsPage",
  components: {
    PageView,
    ContentView,
    ProjectValidationAndSaveHandler,
    ProjetYearAndStatutSelection,
    ProjetLabellisationHolder,
    ProjetInscriptionHolder,
    LoadingSkeleton,
    InstructionPanel,
    BIcon,
  },
  props: {
    projectId: {
      type: String,
      required: true,
    },
    annualProjectId: {
      type: String,
      required: true,
    },
    section: {
      type: String,
      default: "bref",
    },
    activeTab: {
      type: Number,
      default: 0,
    },
  },

  emits: ["project-saved"],
  data() {
    return {
      // Services
      infoReportingService: InfoReportingService.INSTANCE,
      loginService: LoginService.INSTANCE,
      projetService: ProjectService.INSTANCE,
      usersService: UsersService.INSTANCE,
      instructionService: InstructionService.INSTANCE,

      // Component state
      dirty: false,
      withRedirect: false,
      loggedUser: new UtilisateurDTO() as UtilisateurDTO | undefined,
      isLoading: true,
      isSaving: false,
      project: new ProjetAnnuelDTO(),
      lastSaveProject: new ProjetAnnuelDTO(),
      readonly: false,
      isPorteurProjet: false,
      canSeeInstructionPanel: false,
      currentWidth: 0,
      instructionPanelWidth: 0,
      instructionPanelAutoCloseWidth: 350,
      currentScrollY: 0,
      pdfExportRequested: false,
      mapScreenshotRequested: false,
      exportingPdf: false,
      exportingPdfDone: false,
      withPhotos: false,
      pxFromBottom: 900,
      instructionCollapsed: true,
      showValidationErrors: false,
      mapDataUrl: "" as string | null,
      imagesUrl: [] as any[],
      isInitialLoad: true,

      // Projet state & periods
      etats: [] as EtatProjet[],
      etatsInitiaux: ["ACTIF", "EN_PAUSE", "ARCHIVE"] as EtatProjet[],

      /**************** VALIDATION ****************/
      // Indicates if validation should be launched or not
      validationActivated: false,
      // Last validation results
      lastProjectValidation: new ProjetAnnualValidation(),
      // Last validation date (used to requets new validations to the ProjectValidationAndSaveHandler)
      lastValidationDate: new Date(),

      modalShown: false,
      mainTitle: "",
      isActive: true,
      // mobileShow = true
      role: "",
      // Debounced validation trigger to avoid performance issues
      debouncedTriggerValidation: null as ReturnType<typeof debounce> | null,
    }
  },
  watch: {
    canSeeInstructionPanel: {
      handler(): void {
        this.$nextTick(this.activateResizablePanel)
      },
      immediate: true,
    },
    annualProjectId: {
      handler(): void {
        this.reloadProjectFromServer()
      },
    },
    section: {
      handler(): void {
        this.$nextTick(() => {
          // `project-content` is the effective scroll container in project pages.
          const projectContent = this.getProjectContentElement()
          if (projectContent) {
            projectContent.scrollTo({ top: 0, left: 0, behavior: "auto" })
          }

          const leftPane = document.getElementById("panel-left")
          leftPane?.scrollTo(0, 0)

          window.focus()
          window.scrollTo(0, 0)
        })

        // Update validation
        this.triggerValidation()

        this.updateMainPageTitle()
      },
    },
  },

  async created(): Promise<void> {
    // Initialize debounced validation trigger (800ms delay for smooth typing)
    this.debouncedTriggerValidation = debounce(() => {
      if (this.validationActivated) {
        this.lastValidationDate = new Date()
      }
    }, 800)

    // Écouter l'événement léger "input-dirty" pour activer le bouton Enregistrer
    // dès la première frappe, sans déclencher de validation lourde
    eventBus.on("input-dirty", this.onInputDirty)

    const loggedUser = await this.loginService.getLoggedUser()
    if (!loggedUser || !loggedUser.id) {
      return
    }

    this.loggedUser = loggedUser
    this.role = this.loggedUser ? this.loggedUser.profil : PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR
    await this.reloadProjectFromServer()
    this.listenToSubTitleEdition()
    document.getElementById("menu")?.addEventListener("menu-expand", () => {
      this.collapseResizablePanel(this.instructionCollapsed, true, false)
    })
  },
  mounted(): void {
    this.activateResizablePanel()

    const projectContent = this.getProjectContentElement()
    if (projectContent) {
      // Listen to scroll to be able to update active anchor
      projectContent.addEventListener("scroll", this.trackScroll)
      this.setCurrentScrollYAndComputepxFromBottom(projectContent.scrollTop)
      // Handle anchor navigation
      projectContent.addEventListener("click", this.handleAnchorClick)
    }

    window.addEventListener("resize", this.activateResizablePanel)
    this.isActive = true
  },
  unmounted(): void {
    this.isActive = false
    // Cancel pending debounced validation
    if (this.debouncedTriggerValidation) {
      this.debouncedTriggerValidation.cancel()
    }
    eventBus.off("input-dirty", this.onInputDirty)
    const projectContent = this.getProjectContentElement()
    projectContent?.removeEventListener("scroll", this.trackScroll)
    projectContent?.removeEventListener("click", this.handleAnchorClick)
    window.removeEventListener("resize", this.activateResizablePanel)
  },
  methods: {
    getProjectContentElement(): HTMLElement | null {
      return (this.$refs.projectContent as HTMLElement | undefined) ?? null
    },

    handleAnchorClick(event: Event): void {
      const target = event.target as HTMLElement
      const link = target.closest("a[href^='#']") as HTMLAnchorElement | null

      if (link && link.href.startsWith("#")) {
        event.preventDefault()
        const anchorId = link.href.substring(1) // Enlever le #
        const element = document.getElementById(anchorId)

        if (element) {
          const projectContent = this.getProjectContentElement()
          if (projectContent) {
            const elementPosition = element.getBoundingClientRect()
            const containerPosition = projectContent.getBoundingClientRect()
            const scrollPosition =
              projectContent.scrollTop + (elementPosition.top - containerPosition.top)

            projectContent.scrollTo({
              top: scrollPosition,
              behavior: "smooth",
            })
          }
        }
      }
    },
    updateProject(updatedProject: ProjetAnnuelDTO): void {
      // Check if the project actually changed before marking as dirty
      const projectDidChange =
        JSON.stringify(updatedProject) !== JSON.stringify(this.lastSaveProject)
      this.project = updatedProject
      // Only mark as dirty if there's a real difference from last saved state
      if (projectDidChange) {
        this.dirty = true
      }
    },

    async getProjetFromId(
      loggeduser: UtilisateurDTO | undefined,
      annualProjectId?: string,
    ): Promise<void> {
      try {
        this.isPorteurProjet = this.usersService.isPorteurProjet(this.loggedUser)
        this.canSeeInstructionPanel = this.projetService.canSeeInstructionPanel(this.loggedUser)

        if (this.canSeeInstructionPanel) {
          this.project = await this.instructionService.getProject(annualProjectId)
        } else {
          this.project = await this.projetService.getProjetAnnuel(annualProjectId)
        }

        this.lastSaveProject = { ...this.project }
        this.etats = this.etatsInitiaux.filter((etat) => etat !== this.project.etat)
        this.isLoading = false

        // Restaurer l'état du panneau depuis localStorage si ce n'est pas le chargement initial
        if (!this.isInitialLoad && this.canSeeInstructionPanel) {
          const savedPanelState = localStorage.getItem(
            "instruction-panel-state-" + this.projectId + "-" + loggeduser?.id,
          )
          if (savedPanelState) {
            const panelState = JSON.parse(savedPanelState)
            this.instructionCollapsed = panelState.collapsed
            this.instructionPanelWidth = panelState.width
          }
        }

        // Compute readonly
        this.readonly = this.projetService.isProjetReadOnly(this.loggedUser, this.project, true)
        if (this.readonly) {
          this.validationActivated = false
          this.showValidationErrors = !this.isPorteurProjet
        } else if (this.project.typeDossier == "DOSSIER_INSCRIPTION") {
          // If not read-only, inscription validation should be on
          this.validationActivated = true
          this.showValidationErrors = false
        } else {
          // Remember if project validation should be launched
          this.validationActivated =
            localStorage.getItem("validate-project-" + this.projectId + "-" + loggeduser?.id) ==
            "true"
          this.showValidationErrors = this.validationActivated
        }
        // Project may have changed, relaunch validation if validation is activated
        this.$nextTick(this.activateResizablePanel)
        this.$nextTick(this.triggerValidation)

        // Marquer que le chargement initial est terminé
        this.isInitialLoad = false
      } catch (e: unknown) {
        this.infoReportingService.error(this.$t("projet.not-allowed").toString(), e as Error)
        await this.$router.push(
          LoginService.INSTANCE.getPageToRedirectToAfterLogin(this.loggedUser),
        )
      }

      this.updateMainPageTitle()
    },

    async saveProject(): Promise<void> {
      this.isSaving = true
      try {
        this.formatDataBeforeSaving(this.project)
        const savedProject = await this.projetService.saveProject(this.project)
        this.updateProject(savedProject)
        eventBus.emit("project-saved")

        this.lastSaveProject = { ...this.project }
        this.dirty = false
        this.isSaving = false
        this.infoReportingService.success(this.$t("projet.save-success").toString())

        if (this.withRedirect) {
          await this.$router.push("/myProjects")
        }
        this.withRedirect = false
      } catch (e: any) {
        this.infoReportingService.error(this.$t("projet.save-error").toString(), e as Error)
        this.isSaving = false
        this.dirty = true
      }
    },

    async sendInscription(): Promise<void> {
      try {
        this.isSaving = true
        this.project = await this.projetService.submitProjetInscription(
          this.project as ProjetInscriptionDTO,
        )
        this.isSaving = false
        this.infoReportingService.success(this.$t("projet.submit-inscription-sent").toString())
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("projet.submit-inscription-error").toString(),
          e as Error,
        )
        this.isSaving = false
      }
    },

    doMapScreenshot(): void {
      this.mapScreenshotRequested = true
    },

    clearMapScreenshot(): void {
      this.mapDataUrl = null
    },

    screenshotRequestDone(dataUrl: string | null): void {
      this.mapScreenshotRequested = false
      this.mapDataUrl = dataUrl
    },

    async sendLabellisation(): Promise<void> {
      try {
        this.isSaving = true
        this.project = await this.projetService.submitProjetLabellisation(this.project)
        this.isSaving = false
        this.infoReportingService.success(this.$t("projet.submit-labellisation-success").toString())
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("projet.submit-labellisation-error").toString(),
          e as Error,
        )
        this.isSaving = false
      }
    },

    onInputDirty(): void {
      // Lightweight handler: just mark as dirty without JSON comparison or validation
      if (!this.dirty && !this.readonly) {
        this.dirty = true
      }
    },

    dirtifyProject(withRedirect = false): void {
      // If not already dirty, determine if we are now dirty
      if (!this.dirty) {
        this.showValidationErrors = this.validationActivated
        const projectDidChange =
          JSON.stringify(this.project) != JSON.stringify(this.lastSaveProject)

        if (projectDidChange) {
          this.dirty = true
          this.withRedirect = this.withRedirect || withRedirect
        }
      }
    },

    async reloadProjectFromServer(): Promise<void> {
      // Reload project as posted on server
      this.isLoading = true
      await this.getProjetFromId(this.loggedUser, this.annualProjectId)
    },

    projetAnnuelValidationChanged(validation: ProjetAnnualValidation): void {
      this.lastProjectValidation = validation
    },

    updateMainPageTitle(): void {
      // Update page title : use accompagneur version if relevant other wise default tile
      let accompagneurMainTitle = null
      if (this.loggedUser && !this.usersService.isPorteurProjet(this.loggedUser)) {
        accompagneurMainTitle = this.$t(`projet.menu.${this.section}-accompagnateur`).toString()
      }
      if (accompagneurMainTitle && accompagneurMainTitle.indexOf("-accompagnateur") == -1) {
        // Use renewal suffix if relevant
        if (this.project.typeDossier !== "DOSSIER_LABELLISATION") {
          const renewalMainTitle = this.$t(
            `projet.menu.${this.section}-accompagnateur-renewal`,
          ).toString()
          if (renewalMainTitle && renewalMainTitle.indexOf("-renewal") == -1) {
            this.mainTitle = renewalMainTitle
          } else {
            this.mainTitle = accompagneurMainTitle
          }
        } else {
          this.mainTitle = accompagneurMainTitle
        }
      } else {
        // Use renewal suffix if relevant
        if (this.project.typeDossier !== "DOSSIER_LABELLISATION") {
          const renewalMainTitle = this.$t(`projet.menu.${this.section}-renewal`).toString()
          if (renewalMainTitle && renewalMainTitle.indexOf("-renewal") == -1) {
            this.mainTitle = renewalMainTitle
          } else {
            this.mainTitle = this.$t(`projet.menu.${this.section}`).toString()
          }
        } else {
          this.mainTitle = this.$t(`projet.menu.${this.section}`).toString()
        }
      }
    },

    triggerValidation(): void {
      // Use debounced version to avoid performance issues during typing
      if (this.debouncedTriggerValidation) {
        this.debouncedTriggerValidation()
      }
    },

    // Immediate validation trigger for cases where we need instant feedback
    triggerValidationImmediate(): void {
      if (this.validationActivated) {
        this.lastValidationDate = new Date()
      }
    },

    togglevalidation(): void {
      this.validationActivated = !this.validationActivated
      this.showValidationErrors = this.validationActivated
      // Remember that validation was activated or not so that user does n
      localStorage.setItem(
        "validate-project-" + this.projectId + "-" + this.loggedUser?.id,
        this.validationActivated.toString(),
      )
      // Use immediate trigger when toggling validation for instant feedback
      this.$nextTick(this.triggerValidationImmediate)
    },

    listenToSubTitleEdition(): void {
      eventBus.on("sub-title-edited", (editedSubTitle: string) => {
        if (this.isActive) {
          this.project.nom = editedSubTitle
          const patch = { nom: editedSubTitle } as ProjetPatchDTO
          this.projetService
            .patchProject(this.projectId, patch)
            .then(() => this.reloadProjectFromServer())
        }
      })
    },

    // A function is used for dragging and moving
    activateResizablePanel(): void {
      const galery = document.getElementById("carousel-gallery-column")
      const leftPane = document.getElementById("panel-left")
      const separator = document.getElementById("panel-separator")
      const rightPane = document.getElementById("panel-right")

      if (galery && leftPane) {
        galery.style.maxWidth = Math.floor(leftPane.offsetWidth / 2 - 15) + "px"
      }

      if (leftPane && separator && rightPane) {
        // Attendre que le DOM soit stable avant de calculer les dimensions
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            // Ne réinitialiser que lors du premier chargement ou si les dimensions sont invalides
            if (this.isInitialLoad || this.instructionPanelWidth === 0) {
              this.instructionPanelWidth = rightPane.offsetWidth
              this.currentWidth = leftPane.offsetWidth
              this.collapseResizablePanel(false, false, true)
            }
            this.relocateProjectButtonsAndGalery(rightPane.clientWidth + "px", leftPane.offsetWidth)
          })
        })

        separator.onmousedown = (e: Event) => {
          const maxWidth = Math.max(
            (leftPane.offsetWidth + rightPane.offsetWidth + separator.offsetWidth) / 2,
            420,
          )
          const minWidth = 64
          const md = {
            e,
            offsetLeft: separator.offsetLeft,
            offsetTop: separator.offsetTop,
            firstWidth: leftPane.offsetWidth,
            secondWidth: rightPane.offsetWidth,
          }

          document.onmousemove = (e: Event) => {
            if (md && md.e) {
              // @ts-ignore
              const delta = { x: e.clientX - md.e.clientX, y: e.clientY - md.e.clientY }
              // Prevent negative-sized elements
              delta.x = Math.min(Math.max(delta.x, -md.firstWidth), md.secondWidth)

              if (md.secondWidth - delta.x < maxWidth && md.secondWidth - delta.x >= minWidth) {
                separator.style.left = md.offsetLeft + delta.x + "px"
                leftPane.style.width = md.firstWidth + delta.x + "px"
                rightPane.style.width = md.secondWidth - delta.x + "px"

                this.currentWidth = parseInt(leftPane.style.width)
                this.instructionPanelWidth = parseInt(rightPane.style.width)
                this.relocateProjectButtonsAndGalery(
                  rightPane.style.width.toString(),
                  parseInt(leftPane.style.width),
                )
              }
            }
          }
          document.onmouseup = () => {
            document.onmousemove = document.onmouseup = null
            const currentRightPaneWidth = parseInt(rightPane.style.width)
            if (currentRightPaneWidth < this.instructionPanelAutoCloseWidth) {
              this.instructionCollapsed = true
              const fullWidth = leftPane.offsetWidth + rightPane.offsetWidth + separator.offsetWidth
              const leftPaneWidth = fullWidth - separator.offsetWidth - minWidth
              leftPane.style.width = leftPaneWidth + "px"
              separator.style.left = leftPaneWidth + "px"
              rightPane.style.width = minWidth + "px"
              this.currentWidth = parseInt(leftPane.style.width)
              this.instructionPanelWidth = parseInt(rightPane.style.width)
              this.relocateProjectButtonsAndGalery(
                rightPane.style.width.toString(),
                parseInt(leftPane.style.width),
              )
            } else {
              this.instructionCollapsed = false
              this.currentWidth = parseInt(leftPane.style.width)
              this.instructionPanelWidth = parseInt(rightPane.style.width)
            }
            // Sauvegarder l'état du panneau dans localStorage
            this.savePanelState()
          }
        }
      }
    },

    /**
     * Expands or collapses the panel
     */
    collapseResizablePanel(collapse: boolean, force: boolean, initialLoad: boolean): void {
      const leftPane = document.getElementById("panel-left")
      const separator = document.getElementById("panel-separator")
      const rightPane = document.getElementById("panel-right")
      const splitter = document.getElementById("splitter")

      // Si l'utilisateur a explicitement fermé le panneau et qu'on ne force pas l'ouverture,
      // on respecte son choix et on ne fait rien
      if (!collapse && !force && !initialLoad && this.instructionCollapsed) {
        return
      }

      // Déterminer si on doit modifier le panneau
      const shouldModifyPanel =
        collapse || // Demande explicite de fermeture
        force || // Mise à jour forcée
        initialLoad || // Chargement initial
        (!this.instructionCollapsed &&
          this.instructionPanelWidth < this.instructionPanelAutoCloseWidth) // Auto-expand si pas explicitement fermé

      if (shouldModifyPanel) {
        if (separator && leftPane && rightPane && splitter) {
          const fullWidth = splitter.offsetWidth
          let rightWidth = 64
          this.instructionCollapsed = collapse
          if (!collapse) {
            rightWidth = Math.max(fullWidth / 3, 420)
          }
          if (initialLoad && Math.max(fullWidth) < 800) {
            rightWidth = 64
            this.instructionCollapsed = true
          }
          separator.style.left = fullWidth - separator.offsetWidth - rightWidth + "px"
          leftPane.style.width = fullWidth - separator.offsetWidth - rightWidth + "px"
          rightPane.style.width = rightWidth + "px"

          this.currentWidth = parseInt(leftPane.style.width)
          this.instructionPanelWidth = parseInt(rightPane.style.width)
          this.relocateProjectButtonsAndGalery(
            rightPane.style.width.toString(),
            parseInt(leftPane.style.width),
          )
          // Sauvegarder l'état du panneau dans localStorage
          this.savePanelState()
        }
      }
    },

    relocateProjectButtonsAndGalery(rightString: string, leftPaneSize: number): void {
      let right = parseInt(rightString.replace("px", ""))

      // On small resolutions, relcoate projet buttons to make sure 'close' cross is never displayed
      if (leftPaneSize + right < 1300) {
        right += 140
      }
      // On small resolutions, hide projet buttons when panel is expanded
      const hideProjectButtons: boolean = leftPaneSize < 600 && right > 500
      const stateIndicator = document.getElementById("project-state")
        ?.firstChild as HTMLElement | null

      if (stateIndicator) {
        if (!hideProjectButtons) {
          stateIndicator.style.display = "block"
          stateIndicator.style.right = right + "px"
        } else {
          stateIndicator.style.display = "none"
        }
      } else {
        setTimeout(() => {
          const stateIndicator = document.getElementById("project-state")
            ?.firstChild as HTMLElement | null
          if (stateIndicator) {
            if (!hideProjectButtons) {
              stateIndicator.style.display = "block"
              stateIndicator.style.right = right + "px"
            } else {
              stateIndicator.style.display = "none"
            }
          }
        }, 600)
      }

      const galery = document.getElementById("carousel-gallery-column")
      if (galery) {
        galery.style.maxWidth = Math.floor(this.currentWidth / 2 - 15) + "px"
      }
    },

    trackScroll(): void {
      const projectContent = this.getProjectContentElement()
      if (projectContent) {
        this.setCurrentScrollYAndComputepxFromBottom(projectContent.scrollTop)
      }
    },

    setCurrentScrollYAndComputepxFromBottom(scrollY: number): void {
      const projectContent = this.getProjectContentElement()
      const maxHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight,
        projectContent ? projectContent.scrollHeight : 0,
      )
      this.pxFromBottom = 900
      if (scrollY > 0) {
        this.pxFromBottom = maxHeight - window.innerHeight - scrollY
      }
      this.currentScrollY = scrollY
    },

    changeImageUrl(imagesUrl: any): void {
      this.imagesUrl = imagesUrl
    },

    savePanelState(): void {
      // Sauvegarder l'état du panneau d'instruction dans localStorage
      if (this.loggedUser?.id) {
        const panelState = {
          collapsed: this.instructionCollapsed,
          width: this.instructionPanelWidth,
        }
        localStorage.setItem(
          "instruction-panel-state-" + this.projectId + "-" + this.loggedUser.id,
          JSON.stringify(panelState),
        )
      }
    },

    formatDataBeforeSaving(projet: ProjetAnnuelDTO): void {
      // Retire les ids temporaires des classes utilisés pour les vid
      projet.classes?.forEach((classe) => {
        if (classe.id?.startsWith("new-")) {
          classe.id = ""
        }
      })
    },
  },
})
</script>
<style lang="scss">
@import "@/styles/variables";

.projectPage-tabs {
  .tabs {
    margin-top: 1rem;
  }
}

.title-without-margin {
  .title-view {
    margin-top: 0;
  }
}

.item-text {
  text-align: center;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;

  &.bold {
    font-weight: 700;
  }
}

.projet-part {
  h1,
  h2,
  h3 {
    font-family: $font-family-title;
  }

  h2 {
    font-size: 24px;
    padding-bottom: 5px;
    padding-top: 20px;
  }

  h3 {
    font-size: 18px;
    color: $ofb-bleu-1;
  }
}

#project-content {
  overflow: auto;
  padding-top: 1rem;
  padding-bottom: 1rem;
  padding-left: 1vw;
}

.splitter {
  width: 100%;
  height: 100%;
  display: flex;

  .anchor {
    display: block;
    position: relative;
    top: -150px;
    visibility: hidden;
  }
}

#panel-separator {
  cursor: col-resize;
  width: 12px;
  height: 100%;

  /* Prevent the browser's built-in drag from interfering */
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  border-right: 1px solid $instruction-dark;
  display: flex;
  align-items: center;

  .resize-symbol {
    height: 250px;
    width: 12px;
    background-color: $instruction-dark;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='30'><path d='M2 0 v30 M5 0 v30 M8 0 v30' fill='none' stroke='white'/></svg>");
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 10px 0 0 10px;
  }
}

.panel-left {
  width: 80%;
  min-width: 10px;
}

.panel-full {
  width: 100%;
}

.panel-left,
.panel-full {
  height: 100%;
  overflow: hidden;

  .page-container {
    padding: 0;
    height: 100%;

    #content {
      height: 100%;
    }
  }
}

#panel-right {
  background-color: $instruction-background;
  width: max(20%, 420);
  overflow-y: hidden;
  min-width: 10px;

  .panel-right-close {
    position: absolute;
    margin: 5px;
    cursor: pointer;
    border: 1px solid $instruction-dark;
    border-radius: 5px;
    color: $instruction-dark;
    display: flex;

    &:hover {
      background-color: $instruction-dark;
      color: $instruction-background;
      border-color: $instruction-light;
    }
  }
}
</style>
